import React, {
  FormEvent,
  ReactNode,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useRef,
} from 'react';
import styles from './UpdatePassword.module.scss';
import { MessageBanner } from 'components/helpers/Atoms/FormElements/MessageBanner';
import { PasswordInputField } from 'components/helpers/Atoms/FormElements/PasswordInputField';
import { NewPasswordWithCriteria } from 'components/helpers/Atoms/FormElements/NewPasswordWithCriteria';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import useDictionary from 'lib/hooks/useDictionary';
import { EditableTabHandle } from '../shared/EditableTabHandle';
import { EditableTabProps } from '../shared/EditableTabProps';
import SvgIcon from 'components/helpers/SvgIcon/SvgIcon';
import { useFormErrors } from 'lib/hooks/useFormErrors';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';
import { getPasswordRuleStatus, passwordRules } from 'lib/utils/validation/password-validation';
import config from 'sitecore.config';
import { pushToDataLayer, DataLayerEvent } from 'components/helpers/Global/Analytics/DataLayer';

const FORM_FIELD_IDS = {
  CURRENT_PASSWORD: 'currentPassword',
  NEW_PASSWORD: 'newPassword',
  CONFIRM_PASSWORD: 'confirmPassword',
} as const;

export interface UpdatePasswordProps extends EditableTabProps {
  tabLabelNode?: ReactNode | null; // Tab label for default view
  headlineNode?: ReactNode | null; // Edit mode title (editPasswordTitle)
  descriptionNode?: ReactNode | null; // Default view description
  editDescriptionNode?: ReactNode | null; // Edit mode description (editPasswordDescription)
  currentPasswordLabelNode?: ReactNode | null;
  newPasswordLabelNode?: ReactNode | null;
  confirmPasswordLabelNode?: ReactNode | null;
  saveButtonTextNode?: ReactNode | null;
  cancelButtonText?: ReactNode;
  successMessageNode?: ReactNode | null; // Success message after password update
  passwordNotSupportedText?: ReactNode | null; // Message to show when password is not supported
}

// UpdatePasswordHandle extends EditableTabHandle - using type alias for clarity
export type UpdatePasswordHandle = EditableTabHandle;

const UpdatePassword = forwardRef<UpdatePasswordHandle, UpdatePasswordProps>((props, ref) => {
  const {
    tabLabelNode,
    headlineNode,
    descriptionNode,
    editDescriptionNode,
    currentPasswordLabelNode,
    newPasswordLabelNode,
    confirmPasswordLabelNode,
    saveButtonTextNode,
    cancelButtonText = 'Cancel',
    successMessageNode,
    passwordNotSupportedText,
    isEditMode,
    onEditModeChange,
    onRequestCancel, // Callback for parent to handle dialog
  } = props;

  const { data: session, update: updateSession } = useSessionSafe();
  const { getDictionaryValue } = useDictionary();

  // Get hasPassword from session (stored in JWT token during login)
  // Only true if explicitly true, otherwise false
  const hasPassword = session?.user?.hasPassword === true;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<ReactNode | string>('');
  const [submitError, setSubmitError] = useState<string>('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [pendingFocusField, setPendingFocusField] = useState<
    (typeof FORM_FIELD_IDS)[keyof typeof FORM_FIELD_IDS] | null
  >(null);

  // Track password criteria validation state
  const [isNewPasswordMeetingCriteria, setIsNewPasswordMeetingCriteria] = useState<
    boolean | undefined
  >(undefined);

  // Single source of truth for field errors (only custom business logic errors)
  const { setFieldError, clearAllErrors, getFieldError } = useFormErrors();

  // Refs for form fields (for focus management)
  const currentPasswordRef = useRef<HTMLInputElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const getNewPasswordCriteriaError = useCallback(
    (passwordValue: string): string | null => {
      const trimmed = passwordValue.trim();
      if (trimmed.length === 0) return null;

      const ruleStatus = getPasswordRuleStatus(trimmed, passwordRules);
      const failedRules = passwordRules.filter((rule) => !ruleStatus[rule.id]);
      if (failedRules.length === 0) return null;

      const siteName = config.defaultSite;

      if (siteName === 'GMFS') {
        return getDictionaryValue(
          'Components.MyAccount.ErrorMessages.PasswordInlineError',
          'Password must be at least 8 characters and contain at least 3 of the following types: uppercase letter, lowercase letter, number and symbol.'
        );
      }

      const ruleLabels = failedRules.map((rule) =>
        rule.dictionaryKey ? getDictionaryValue(rule.dictionaryKey, rule.label) : rule.label
      );

      const passwordMust = getDictionaryValue('Components.MyAccount.PasswordMust', 'Password must');

      if (ruleLabels.length === 1) {
        return `${passwordMust} ${ruleLabels[0]}.`;
      }

      if (ruleLabels.length === 2) {
        return `${passwordMust} ${ruleLabels[0]} and ${ruleLabels[1]}.`;
      }

      const labels = [...ruleLabels];
      const lastLabel = labels.pop();
      return `${passwordMust} ${labels.join(', ')}, and ${lastLabel}.`;
    },
    [getDictionaryValue]
  );

  // Let the alert banner announce before moving focus to the first invalid field.
  useEffect(() => {
    if (!pendingFocusField) return;

    const timer = setTimeout(() => {
      if (pendingFocusField === FORM_FIELD_IDS.CURRENT_PASSWORD) {
        currentPasswordRef.current?.focus();
        currentPasswordRef.current?.select();
      } else if (pendingFocusField === FORM_FIELD_IDS.NEW_PASSWORD) {
        newPasswordRef.current?.focus();
        newPasswordRef.current?.select();
      } else if (pendingFocusField === FORM_FIELD_IDS.CONFIRM_PASSWORD) {
        confirmPasswordRef.current?.focus();
        confirmPasswordRef.current?.select();
      }

      setPendingFocusField(null);
    }, 100);

    return () => clearTimeout(timer);
  }, [pendingFocusField]);

  const resetForm = useCallback(() => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setSubmitSuccess('');
    setSubmitError('');
    setHasAttemptedSubmit(false);
    setPendingFocusField(null);
    clearAllErrors();
    setIsNewPasswordMeetingCriteria(undefined);
  }, [clearAllErrors]);

  const handleCancelConfirm = useCallback(() => {
    onEditModeChange(false);
    resetForm();
  }, [onEditModeChange, resetForm]);

  // Check if form has unsaved changes
  const isDirty = useCallback(() => {
    return currentPassword !== '' || newPassword !== '' || confirmPassword !== '';
  }, [currentPassword, newPassword, confirmPassword]);

  // Expose methods to parent component
  useImperativeHandle(
    ref,
    () => ({
      resetForm: () => {
        handleCancelConfirm();
      },
      isDirty,
    }),
    [handleCancelConfirm, isDirty]
  );

  const handleEdit = () => {
    onEditModeChange(true);
    setSubmitSuccess('');
    setSubmitError('');
    setHasAttemptedSubmit(false);
    setPendingFocusField(null);
    clearAllErrors();
    setIsNewPasswordMeetingCriteria(undefined);
  };

  const handleCancel = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    // If parent provides callback, use it (parent will show dialog)
    // Otherwise, directly reset (for standalone usage)
    if (onRequestCancel) {
      onRequestCancel();
    } else {
      handleCancelConfirm();
    }
  };

  // Handle Tab key on submit button to redirect to first error field when there are validation errors
  const handleSubmitButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    // Only handle Tab key (not Shift+Tab)
    if (e.key === 'Tab' && !e.shiftKey && hasAttemptedSubmit) {
      // Check if there are any validation errors
      if (
        getFieldError('currentPassword') ||
        getFieldError('newPassword') ||
        getFieldError('confirmPassword') ||
        isNewPasswordMeetingCriteria === false
      ) {
        // Prevent default tab behavior
        e.preventDefault();

        // Focus the first error field
        if (getFieldError('currentPassword') && currentPasswordRef.current) {
          currentPasswordRef.current.focus();
          currentPasswordRef.current.select();
        } else if (
          (getFieldError('newPassword') || isNewPasswordMeetingCriteria === false) &&
          newPasswordRef.current
        ) {
          newPasswordRef.current.focus();
        } else if (getFieldError('confirmPassword') && confirmPasswordRef.current) {
          confirmPasswordRef.current.focus();
          confirmPasswordRef.current.select();
        }
      }
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitSuccess('');
    setSubmitError('');
    setHasAttemptedSubmit(true);

    // Validate only custom business logic (components handle their own validation)
    clearAllErrors();
    const aggregatedErrors: string[] = [];
    let hasValidationErrors = false;

    // Get mismatch error message (same one used by PasswordInputField component)
    const mismatchErrorMessage = getDictionaryValue(
      'Components.MyAccount.ErrorMessages.PasswordsDoNotMatch',
      'Passwords do not match'
    );

    const emptyFieldsErrorMessage = getDictionaryValue(
      'Components.MyAccount.ErrorMessages.EmptyFieldsSubmission',
      'There was an error updating your password.| Please try again.'
    );

    const criteriaErrorMessage = getNewPasswordCriteriaError(newPassword);
    const hasCriteriaError = Boolean(criteriaErrorMessage);

    // Validate current password - required
    if (!currentPassword.trim()) {
      const requiredMessage = getDictionaryValue(
        'Components.MyAccount.ErrorMessages.CurrentPasswordRequired',
        'Please enter your current password.'
      );
      setFieldError(FORM_FIELD_IDS.CURRENT_PASSWORD, requiredMessage);
      aggregatedErrors.push(requiredMessage);
      hasValidationErrors = true;
    }

    // Validate new password - required, custom business rule, and criteria validation
    if (!newPassword.trim()) {
      const requiredMessage = getDictionaryValue(
        'Components.MyAccount.ErrorMessages.NewPasswordRequired',
        'Please enter your new password.'
      );
      setFieldError(FORM_FIELD_IDS.NEW_PASSWORD, requiredMessage);
      aggregatedErrors.push(requiredMessage);
      hasValidationErrors = true;
    } else if (currentPassword.trim() && newPassword === currentPassword) {
      // Custom validation: new password cannot be same as current
      const sameAsCurrentMessage = getDictionaryValue(
        'Components.MyAccount.ErrorMessages.NewPasswordSameAsCurrent',
        'New password cannot be the same as current password'
      );
      setFieldError(FORM_FIELD_IDS.NEW_PASSWORD, sameAsCurrentMessage);
      aggregatedErrors.push(sameAsCurrentMessage);
      hasValidationErrors = true;
    } else if (hasCriteriaError || isNewPasswordMeetingCriteria === false) {
      // Password doesn't meet criteria - component handles its own error message
      // We prevent submission by checking this in the validation check below
      // The component's internal error will be displayed via showInlineError
      if (criteriaErrorMessage) {
        aggregatedErrors.push(criteriaErrorMessage);
      }
      hasValidationErrors = true;
    }

    // Validate confirm password - required
    // Note: PasswordInputField component handles matching validation internally via valueToMatch
    // We use the component's mismatchErrorMessage to show the error on submit
    if (!confirmPassword.trim()) {
      const requiredMessage = getDictionaryValue(
        'Components.MyAccount.ErrorMessages.ConfirmPasswordRequired',
        'Please confirm your new password.'
      );
      setFieldError(FORM_FIELD_IDS.CONFIRM_PASSWORD, requiredMessage);
      aggregatedErrors.push(requiredMessage);
      hasValidationErrors = true;
    } else if (newPassword !== confirmPassword) {
      // Use the same error message as PasswordInputField component's mismatchErrorMessage
      // This ensures consistency and avoids duplication
      setFieldError(FORM_FIELD_IDS.CONFIRM_PASSWORD, mismatchErrorMessage);
      aggregatedErrors.push(mismatchErrorMessage);
      hasValidationErrors = true;
    }

    // If validation errors or password criteria not met, stop here
    if (hasValidationErrors || hasCriteriaError || isNewPasswordMeetingCriteria === false) {
      const summarizedErrors = Array.from(new Set(aggregatedErrors)).join(' ');
      setSubmitError(
        emptyFieldsErrorMessage
          ? `${emptyFieldsErrorMessage} ${summarizedErrors}`.trim()
          : summarizedErrors
      );

      // Ensure criteria error is shown inline after submit.
      if (hasCriteriaError) {
        setIsNewPasswordMeetingCriteria(false);
      }

      // WCAG 2.4.3: move focus to the first invalid field after banner announcement.
      if (!currentPassword.trim()) {
        setPendingFocusField(FORM_FIELD_IDS.CURRENT_PASSWORD);
      } else if (
        !newPassword.trim() ||
        newPassword === currentPassword ||
        hasCriteriaError ||
        isNewPasswordMeetingCriteria === false
      ) {
        setPendingFocusField(FORM_FIELD_IDS.NEW_PASSWORD);
      } else if (!confirmPassword.trim() || newPassword !== confirmPassword) {
        setPendingFocusField(FORM_FIELD_IDS.CONFIRM_PASSWORD);
      }

      return;
    }

    setIsSubmitting(true);
    setSubmitSuccess('');
    setSubmitError('');

    try {
      const accessToken = session?.user?.accessToken;
      const userEmail = session?.user?.email;

      // NOTE: This would likely never happen, since unauthenticated users are redirected to the login page
      if (!accessToken || !userEmail) {
        setIsSubmitting(false);
        const errorMsg = 'You must be logged in to update your password.';
        setSubmitError(errorMsg);
        return;
      }

      // Call update password API
      const response = await fetch('/api/auth/update-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userEmail,
          currentPassword,
          newPassword,
          accessToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Determine error message based on API response
        const errorMsg =
          data.error === 'INVALID_CURRENT_PASSWORD'
            ? getDictionaryValue(
                'Components.MyAccount.ErrorMessages.InvalidCurrentPassword',
                'The current password you entered is incorrect.'
              )
            : getDictionaryValue(
                'Components.MyAccount.ErrorMessages.PasswordUpdateFailed',
                'Failed to update password. Please try again later.'
              );

        setIsSubmitting(false);
        setSubmitError(errorMsg);

        // Focus current password field for invalid password error (WCAG 2.4.3)
        if (data.error === 'INVALID_CURRENT_PASSWORD' && currentPasswordRef.current) {
          setFieldError(FORM_FIELD_IDS.CURRENT_PASSWORD, errorMsg);
          setPendingFocusField(FORM_FIELD_IDS.CURRENT_PASSWORD);
        }

        return;
      }

      setIsSubmitting(false);
      // Use success message from props if available, otherwise use dictionary fallback
      const successMsg = successMessageNode
        ? successMessageNode
        : getDictionaryValue(
            'Components.UpdatePassword.Success',
            'Your password was successfully updated.'
          );
      const payload: DataLayerEvent = {
        event: 'profile_update',
      };
      pushToDataLayer(payload);
      setSubmitSuccess(successMsg);

      // Update Next.js session with new access token if provided
      if (data.accessToken && updateSession) {
        try {
          await updateSession({
            user: {
              accessToken: data.accessToken,
            },
          });
        } catch (sessionError) {
          console.error('[UpdatePassword] Failed to update session:', sessionError);
        }
      }

      // Exit edit mode and reset form fields
      onEditModeChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setHasAttemptedSubmit(false);
      setPendingFocusField(null);
      clearAllErrors();
    } catch (error) {
      // Log error for debugging (already logged in API)
      console.error('[UpdatePassword] Unexpected error:', error);

      setIsSubmitting(false);
      // Show generic password reset failed message for unexpected errors
      const errorMsg = getDictionaryValue(
        'Components.MyAccount.ErrorMessages.PasswordUpdateFailed',
        'Failed to update password. Please try again later.'
      );
      setSubmitError(errorMsg);
    }
  };

  const passwordPlaceholder = getDictionaryValue(
    'Components.UpdatePassword.MaskedPassword',
    '••••••••••••'
  );

  // If password is not supported, show message instead of password update UI
  if (session?.user && hasPassword === false) {
    return (
      <div className={styles.updatePassword}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerText}>
              {tabLabelNode && <h1 className={styles.title}>{tabLabelNode}</h1>}
            </div>
          </div>
        </div>
        <hr className={styles.divider} />
        {passwordNotSupportedText && (
          <div className={styles.description}>{passwordNotSupportedText}</div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.updatePassword}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerText}>
            {isEditMode
              ? headlineNode && <h2 className={styles.title}>{headlineNode}</h2>
              : tabLabelNode && <h2 className={styles.title}>{tabLabelNode}</h2>}
            {isEditMode
              ? editDescriptionNode && (
                  <div className={styles.description}>{editDescriptionNode}</div>
                )
              : descriptionNode && <div className={styles.description}>{descriptionNode}</div>}
          </div>
          {!isEditMode && (
            <CtaButton
              ctaStyle="link"
              ctaVariant="brand"
              type="button"
              className={styles.editButton}
              onClick={handleEdit}
            >
              <SvgIcon icon="write-review" viewBox="0 0 14 14" className={styles.editIcon} />
              <span>{getDictionaryValue('Components.MyAccount.Edit', 'Edit')}</span>
            </CtaButton>
          )}
        </div>
      </div>

      <hr className={styles.divider} />

      {!isEditMode ? (
        <>
          {submitSuccess && (
            <MessageBanner
              type="success"
              message={submitSuccess}
              className={styles.successBanner}
            />
          )}
          <div className={styles.defaultView}>
            {currentPasswordLabelNode && (
              <label className={styles.readOnlyLabel}>{currentPasswordLabelNode}</label>
            )}
            <div className={styles.readOnlyValue}>
              <span className={styles.maskedPassword}>{passwordPlaceholder}</span>
            </div>
          </div>
        </>
      ) : (
        <>
          <span className={styles.requiredIndicator}>
            {getDictionaryValue('Components.MyAccount.Required', '* Required')}
          </span>

          {submitError && <MessageBanner type="error" message={submitError} />}

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.inputs}>
              <PasswordInputField
                ref={currentPasswordRef}
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                label={currentPasswordLabelNode || 'Current password'}
                required
                showInlineError={hasAttemptedSubmit && !!getFieldError('currentPassword')}
                validateOnChange={false}
                validateOnBlur={false}
                error={
                  hasAttemptedSubmit
                    ? (getFieldError('currentPassword') as string | undefined)
                    : undefined
                }
              />

              <NewPasswordWithCriteria
                ref={newPasswordRef}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                label={newPasswordLabelNode || 'New password'}
                required
                validateOnChange={true}
                validateOnBlur={false}
                showLeftIcon={hasAttemptedSubmit}
                error={
                  hasAttemptedSubmit
                    ? (getFieldError('newPassword') as string | undefined)
                    : undefined
                }
                showInlineError={
                  hasAttemptedSubmit &&
                  (!!getFieldError('newPassword') || isNewPasswordMeetingCriteria === false)
                }
                onValidationChange={(isValid) => {
                  setIsNewPasswordMeetingCriteria(isValid);
                }}
              />
              <PasswordInputField
                ref={confirmPasswordRef}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                label={confirmPasswordLabelNode || 'Confirm new password'}
                required
                showInlineError={hasAttemptedSubmit && !!getFieldError('confirmPassword')}
                validateOnChange={false}
                validateOnBlur={false}
                error={
                  hasAttemptedSubmit
                    ? (getFieldError('confirmPassword') as string | undefined)
                    : undefined
                }
                valueToMatch={newPassword}
                mismatchErrorMessage={getDictionaryValue(
                  'Components.MyAccount.ErrorMessages.PasswordsDoNotMatch',
                  'Passwords do not match'
                )}
              />
            </div>

            <div className={styles.actions}>
              <CtaButton
                ref={submitButtonRef}
                ctaStyle="filled"
                ctaVariant="brand"
                type="submit"
                disabled={isSubmitting}
                onKeyDown={handleSubmitButtonKeyDown}
              >
                {saveButtonTextNode || 'Save changes'}
              </CtaButton>
              <CtaButton
                ctaStyle="link"
                ctaVariant="brand"
                type="button"
                disabled={isSubmitting}
                onClick={handleCancel}
              >
                {cancelButtonText}
              </CtaButton>
            </div>
          </form>
        </>
      )}
    </div>
  );
});

UpdatePassword.displayName = 'UpdatePassword';

export default UpdatePassword;
